import { ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Alert, Button, Input, Modal, Toggle } from "@/components/common";
import {
  useConsentChannels,
  useCreateClient,
  useRequestConsent,
  useUploadPaperConsent,
} from "@/hooks/useClients";
import type { ConsentRequest } from "@/types/clients";
import { type ChannelChoice, ChannelPicker } from "./ChannelPicker";
import { isValidNip, normalizeNip } from "./nip";
import {
  emptyPaperForm,
  isPaperFormValid,
  PaperConsentFields,
  type PaperConsentForm,
  toPaperPayload,
} from "./PaperConsentFields";
import { isValidPesel } from "./pesel";

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called once the client exists and an electronic request has been sent. */
  onRequestSent: (request: ConsentRequest, clientName: string) => void;
}

export function AddClientModal({
  isOpen,
  onClose,
  onRequestSent,
}: AddClientModalProps) {
  const createClient = useCreateClient();
  const requestConsent = useRequestConsent();
  const uploadPaper = useUploadPaperConsent();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [pesel, setPesel] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [nip, setNip] = useState("");
  const [includesHealth, setIncludesHealth] = useState(false);
  const [paper, setPaper] = useState<PaperConsentForm>(emptyPaperForm);
  // If the client was created but the second step failed, a retry must not create it again.
  const [createdClient, setCreatedClient] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // null = follow the default (e-mail if given); set once the agent picks explicitly.
  const [pickedChannel, setPickedChannel] = useState<ChannelChoice | null>(
    null,
  );

  const hasEmail = email.trim().length > 0;
  const { data: channels } = useConsentChannels();
  const smsAvailable = channels?.sms ?? false;
  const allowed = (c: ChannelChoice) =>
    c === "Paper"
      ? true
      : c === "Email"
        ? hasEmail
        : c === "Sms"
          ? smsAvailable
          : hasEmail && smsAvailable;
  // null = the agent has to choose (no e-mail and no SMS gateway: paper is the only way).
  const channel: ChannelChoice | null =
    pickedChannel && allowed(pickedChannel)
      ? pickedChannel
      : hasEmail
        ? "Email"
        : smsAvailable
          ? "Sms"
          : null;
  const isPaper = channel === "Paper";

  const peselError =
    pesel.length === 11 && !isValidPesel(pesel)
      ? "Nieprawidłowy numer PESEL (suma kontrolna lub data)."
      : undefined;

  const nipError =
    nip.trim() && normalizeNip(nip).length >= 10 && !isValidNip(nip)
      ? "Nieprawidłowy NIP — cyfra kontrolna się nie zgadza."
      : undefined;

  function handleClose() {
    setFirstName("");
    setLastName("");
    setPesel("");
    setPhone("");
    setEmail("");
    setNip("");
    setIncludesHealth(false);
    setPaper(emptyPaperForm());
    setPickedChannel(null);
    setCreatedClient(null);
    createClient.reset();
    requestConsent.reset();
    uploadPaper.reset();
    onClose();
  }

  function sendConsent(client: { id: string; name: string }) {
    if (channel === "Paper") {
      uploadPaper.mutate(
        { clientId: client.id, payload: toPaperPayload(paper) },
        {
          onSuccess: () => {
            toast.success(`Dodano klienta ${client.name} ze zgodą papierową.`);
            handleClose();
          },
        },
      );
      return;
    }
    requestConsent.mutate(
      {
        clientId: client.id,
        includesHealth,
        channel: channel ?? undefined,
      },
      {
        onSuccess: (request) => {
          handleClose();
          onRequestSent(request, client.name);
        },
      },
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (createdClient) {
      sendConsent(createdClient);
      return;
    }
    createClient.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        pesel,
        phone,
        email: email.trim() || null,
        nip: nip.trim() ? normalizeNip(nip) : null,
      },
      {
        onSuccess: (client) => {
          const created = {
            id: client.id,
            name: `${client.firstName} ${client.lastName}`,
          };
          setCreatedClient(created);
          sendConsent(created);
        },
      },
    );
  }

  const error = createClient.error ?? requestConsent.error ?? uploadPaper.error;
  const isPending =
    createClient.isPending || requestConsent.isPending || uploadPaper.isPending;
  const clientDataValid =
    !!firstName.trim() &&
    !!lastName.trim() &&
    isValidPesel(pesel) &&
    !!phone.trim() &&
    (!nip.trim() || isValidNip(nip));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Nowy klient — zgoda RODO"
      className="max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert variant="error">
            {(error as { message?: string }).message ??
              "Nie udało się dodać klienta"}
            {createdClient &&
              " Klient został już dodany — popraw dane zgody i spróbuj ponownie."}
          </Alert>
        )}

        <fieldset disabled={!!createdClient} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Imię"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
              }}
              required
              maxLength={100}
              autoComplete="off"
            />
            <Input
              label="Nazwisko"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
              }}
              required
              maxLength={100}
              autoComplete="off"
            />
          </div>

          <Input
            label="PESEL"
            value={pesel}
            onChange={(e) => {
              setPesel(e.target.value.replace(/\D/g, "").slice(0, 11));
            }}
            inputMode="numeric"
            required
            autoComplete="off"
            error={peselError}
            hint="Zaszyfrowany; na liście widać tylko część."
          />

          <Input
            label="NIP (opcjonalnie)"
            value={nip}
            onChange={(e) => {
              setNip(e.target.value.slice(0, 16));
            }}
            autoComplete="off"
            error={nipError}
            hint="Dla klienta prowadzącego działalność, np. 526-000-12-46."
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Input
              label="Telefon klienta"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
              }}
              required
              placeholder="600 100 200"
              autoComplete="off"
              hint="Musi należeć do klienta."
            />
            <Input
              label="E-mail (opcjonalnie)"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              autoComplete="off"
              hint="Zalecany kanał wysyłki wniosku."
            />
          </div>
          <p className="text-xs text-faint">
            Imię i nazwisko zapiszemy z wielkiej litery (np. „JAN KOWALSKI” →
            „Jan Kowalski”).
          </p>
        </fieldset>

        <ChannelPicker
          value={channel}
          onChange={setPickedChannel}
          hasEmail={hasEmail}
          smsAvailable={smsAvailable}
        />

        {isPaper ? (
          <PaperConsentFields value={paper} onChange={setPaper} />
        ) : (
          <Toggle
            checked={includesHealth}
            onChange={setIncludesHealth}
            label="Oferta na życie lub zdrowotna"
            description="Dołącza osobną zgodę na przetwarzanie danych o zdrowiu (art. 9 RODO)."
          />
        )}

        {!channel && (
          <Alert variant="warning">
            Klient nie ma e-maila, a wysyłka SMS jeszcze nie działa — podaj
            e-mail albo wybierz zgodę papierową.
          </Alert>
        )}

        {channel && !isPaper && (
          <p className="text-sm text-sub">
            Klient dostanie{" "}
            <strong>
              {channel === "Email"
                ? "e-mail"
                : channel === "Sms"
                  ? "SMS"
                  : "SMS i e-mail"}
            </strong>{" "}
            z linkiem i osobnym kodem do każdej zgody. Nie sugeruj mu wyboru
            zgód.
          </p>
        )}

        {/* Sticky so the actions stay visible on short screens. */}
        <div className="sticky -bottom-4 -mx-6 -mb-4 flex justify-end gap-3 border-t border-edge-soft bg-panel px-6 py-4">
          <Button variant="ghost" type="button" onClick={handleClose}>
            Anuluj
          </Button>
          <Button
            type="submit"
            isLoading={isPending}
            leftIcon={<ShieldCheck size={16} aria-hidden />}
            disabled={
              !clientDataValid ||
              !channel ||
              (isPaper && !isPaperFormValid(paper))
            }
          >
            {isPaper
              ? "Dodaj i zapisz zgodę papierową"
              : channel === "Email"
                ? "Dodaj i wyślij e-mail"
                : "Dodaj i wyślij SMS"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
